import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Heading, Button, Flex, Grid, GridItem, useDisclosure } from '@chakra-ui/react';
import { formatDateTime } from 'utils/date.util';

import {
  getSupplierApi,
  updateSupplierApi,
  uploadShippingApi,
  getSftpConnectionsApi,
} from 'features/suppliers/suppliers.api';
import { uploadInventoryApi } from 'features/inventories/inventories.api';

import InventoryUpload from './components/InventoryUpload';
import ShippingUpload from './components/ShippingUpload';
import WeightShipping from './components/WeightShipping';
import Settings from './components/Settings';
import ShippingPricesModal from './components/ShippingPricesModal';
import { useColors } from './components/colors';

const Supplier = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { textColor } = useColors();

  const {
    supplier,
    updateSupplierStatus,
    uploadShippingStatus,
    sftpConnections,
  } = useSelector((state) => state.suppliers);
  const { uploadInventoryStatus } = useSelector((state) => state.inventories);

  const [formData, setFormData] = useState({
    name: '',
    altName: '',
    skuPrefix: '',
    backupInventorySubtractionQuantity: 1,
    sftpConnectionId: '',
    inventoryDateSource: 'filename',
    inventoryFileName: '',
    sftpFileRetentionDays: '',
    usesSubWarehouse: false,
    subWarehouseLabel: 'Sub-warehouse',
    subWarehouseCodesText: '',
  });
  const [weights, setWeights] = useState([]);
  const [inventoryFile, setInventoryFile] = useState(null);
  const [shippingFile, setShippingFile] = useState(null);
  const {
    isOpen: isShippingPricesOpen,
    onOpen: onOpenShippingPrices,
    onClose: onCloseShippingPrices,
  } = useDisclosure();

  useEffect(() => {
    if (id) {
      dispatch(getSupplierApi(id));
    }
    dispatch(getSftpConnectionsApi(false));
  }, [dispatch, id]);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        altName: supplier.altName || '',
        skuPrefix: supplier.skuPrefix || '',
        backupInventorySubtractionQuantity:
          supplier.backupInventorySubtractionQuantity || 1,
        sftpConnectionId: supplier.sftpConnectionId || '',
        inventoryDateSource: supplier.inventoryDateSource || 'filename',
        inventoryFileName: supplier.inventoryFileName || '',
        sftpFileRetentionDays:
          supplier.sftpFileRetentionDays != null
            ? supplier.sftpFileRetentionDays
            : '',
        usesSubWarehouse: !!supplier.usesSubWarehouse,
        subWarehouseLabel: supplier.subWarehouseLabel || 'Sub-warehouse',
        subWarehouseCodesText: Array.isArray(supplier.subWarehouseCodes)
          ? supplier.subWarehouseCodes.join(', ')
          : '',
      });

      if (supplier.weightShipping && supplier.weightShipping.length > 0) {
        setWeights(
          supplier.weightShipping.map((param) => ({
            min: param.minWeight || '',
            max: param.maxWeight || '',
            cost: param.cost || '',
          })),
        );
      }
    }
  }, [supplier]);

  const handleWeightChange = (index, field, value) => {
    const newWeights = [...weights];
    newWeights[index][field] = value;
    setWeights(newWeights);
  };

  const addWeightRow = () => {
    setWeights([...weights, { min: '', max: '', cost: '' }]);
  };

  const removeWeightRow = (index) => {
    const newWeights = weights.filter((_, i) => i !== index);
    setWeights(newWeights);
  };

  const handleInventoryFileChange = (e) => {
    const file = e.target.files[0];
    setInventoryFile(file);
  };

  const handleShippingFileChange = (e) => {
    const file = e.target.files[0];
    setShippingFile(file);
  };

  const clearInventoryFile = () => {
    setInventoryFile(null);
  };

  const clearShippingFile = () => {
    setShippingFile(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateWeights = (weights) => {
    return weights.every((weight) => {
      const min = Number(weight.min);
      const max = Number(weight.max);
      const cost = Number(weight.cost);

      return (
        !isNaN(min) && !isNaN(max) && !isNaN(cost) && min >= 0 && max > min && cost >= 0
      );
    });
  };

  const handleSave = () => {
    if (weights.length > 0 && !validateWeights(weights)) {
      return;
    }

    const formattedWeights = weights.map((weight) => ({
      minWeight: Number(weight.min),
      maxWeight: Number(weight.max),
      cost: Number(weight.cost),
    }));

    const subWarehouseCodes = (formData.subWarehouseCodesText || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    const updateData = {
      skuPrefix: formData.skuPrefix,
      usesSubWarehouse: !!formData.usesSubWarehouse,
      subWarehouseLabel: formData.subWarehouseLabel || 'Sub-warehouse',
      subWarehouseCodes: formData.usesSubWarehouse ? subWarehouseCodes : [],
      backupInventorySubtractionQuantity: Number(
        formData.backupInventorySubtractionQuantity,
      ),
      weightShipping: formattedWeights,
      sftpConnectionId: formData.sftpConnectionId || undefined,
      inventoryDateSource: formData.inventoryDateSource || 'filename',
      inventoryFileName:
        formData.inventoryDateSource === 'lastModified'
          ? formData.inventoryFileName || undefined
          : undefined,
      sftpFileRetentionDays:
        formData.inventoryDateSource === 'filename' &&
        formData.sftpConnectionId &&
        formData.sftpFileRetentionDays !== ''
          ? Number(formData.sftpFileRetentionDays) || undefined
          : undefined,
    };

    dispatch(
      updateSupplierApi({
        id,
        data: updateData,
      }),
    );
  };

  const handleInventoryUpload = async () => {
    if (!inventoryFile) return;

    const result = await dispatch(
      uploadInventoryApi({
        file: inventoryFile,
        supplierId: id,
        supplierName: supplier.name,
      }),
    );

    if (uploadInventoryApi.fulfilled.match(result)) {
      setInventoryFile(null);
    }
  };

  const handleShippingUpload = async () => {
    if (!shippingFile) return;

    const result = await dispatch(
      uploadShippingApi({
        id,
        file: shippingFile,
      }),
    );

    if (uploadShippingApi.fulfilled.match(result)) {
      setShippingFile(null);
    }
  };

  return (
    <Box px={{ base: 4, md: 10 }} py={{ base: 6, md: 8 }} minH="100vh" mt="50px">
      <Heading size="xl" mb={6} color={textColor}>
        {formData.altName || 'Supplier'}
      </Heading>

      <Grid templateColumns="repeat(2, 1fr)" gap={8} mb={8}>
        <GridItem>
          <InventoryUpload
            inventoryFile={inventoryFile}
            onFileChange={handleInventoryFileChange}
            onClearFile={clearInventoryFile}
            onUpload={handleInventoryUpload}
            isUploading={uploadInventoryStatus === 'pending'}
            lastUploadDate={formatDateTime(supplier?.inventoryUpdatedDate) || '-'}
          />
        </GridItem>

        <GridItem>
          <ShippingUpload
            shippingFile={shippingFile}
            onFileChange={handleShippingFileChange}
            onClearFile={clearShippingFile}
            onUpload={handleShippingUpload}
            isUploading={uploadShippingStatus === 'pending'}
            lastUploadDate={formatDateTime(supplier?.shippingUpdatedDate) || '-'}
            onViewPrices={onOpenShippingPrices}
          />
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={8} mb={8}>
        <GridItem>
          <WeightShipping
            weights={weights}
            onWeightChange={handleWeightChange}
            onRemoveWeight={removeWeightRow}
          />
        </GridItem>

        <GridItem>
          <Settings
            formData={formData}
            onInputChange={handleInputChange}
            sftpConnections={sftpConnections || []}
          />
        </GridItem>
      </Grid>

      <ShippingPricesModal
        isOpen={isShippingPricesOpen}
        onClose={onCloseShippingPrices}
        supplierId={id}
        supplierName={formData.altName || supplier?.name}
      />

      <Box display="flex">
        <Flex w="49%" justify="flex-end">
          <Button colorScheme="blue" size="md" w="200px" onClick={addWeightRow}>
            Add
          </Button>
        </Flex>
        <Flex w="51%" justify="flex-end">
          <Button
            colorScheme="blue"
            size="md"
            w="200px"
            onClick={handleSave}
            isLoading={updateSupplierStatus === 'pending'}
          >
            Save
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

export default Supplier;
